/**
 * 卖家精灵「关键词挖掘」xlsx → TemplateImportRow[] (docs/tech/12 §6). Reads the first
 * sheet, maps columns by header name; when the keyword column is not recognized the
 * UI asks the user to pick the columns (TP-02).
 */
import { type TemplateImportRow, toAmazonKeywordText } from "@adpilot/shared";
import type { CellValue, SheetData } from "read-excel-file/browser";

export const IMPORT_FIELDS = [
  "keyword",
  "translation",
  "relevance",
  "abaMonthlyRank",
  "monthlySearchVolume",
  "monthlyPurchases",
  "ppcBid",
  "category",
] as const;
export type ImportField = (typeof IMPORT_FIELDS)[number];

/** Column index per field; missing = not mapped. */
export type ColumnMapping = Partial<Record<ImportField, number>>;

const HEADER_ALIASES: Record<ImportField, string[]> = {
  keyword: ["关键词", "keyword", "keywords", "searchterm", "searchterms", "搜索词"],
  translation: ["关键词翻译", "translation"],
  relevance: ["相关度", "relevance"],
  abaMonthlyRank: ["aba月排名", "abarank", "abamonthlyrank"],
  monthlySearchVolume: ["月搜索量", "searchvolume", "monthlysearchvolume"],
  monthlyPurchases: ["月购买量", "purchases", "monthlypurchases"],
  ppcBid: ["ppc竞价", "ppcbid"],
  category: ["所属类目", "category"],
};

const normalizeHeader = (value: unknown) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[\s_\-()（）]/g, "");

export interface KeywordSheet {
  headers: string[];
  rows: SheetData;
  mapping: ColumnMapping;
}

export function detectColumns(headers: readonly string[]): ColumnMapping {
  const normalized = headers.map(normalizeHeader);
  const mapping: ColumnMapping = {};
  for (const field of IMPORT_FIELDS) {
    const index = normalized.findIndex((header) => HEADER_ALIASES[field].includes(header));
    if (index >= 0) mapping[field] = index;
  }
  return mapping;
}

export async function readKeywordSheet(file: Blob): Promise<KeywordSheet> {
  // Lazy: keeps the xlsx parser out of the wizard's initial chunk.
  const { readSheet } = await import("read-excel-file/browser");
  const [header = [], ...rows] = await readSheet(file);
  const headers = header.map((cell) => String(cell ?? "").trim());
  return { headers, rows, mapping: detectColumns(headers) };
}

/** Excel column letter of a 0-based index: 0 → A, 26 → AA. */
export function columnLetter(index: number): string {
  let letter = "";
  for (let n = index + 1; n > 0; n = Math.floor((n - 1) / 26)) {
    letter = String.fromCharCode(65 + ((n - 1) % 26)) + letter;
  }
  return letter;
}

function toNumber(cell: CellValue | null | undefined): number | null {
  if (typeof cell === "number") return Number.isFinite(cell) ? cell : null;
  if (typeof cell !== "string") return null;
  // `$1.44`, `1,234`, `-`
  const parsed = Number.parseFloat(cell.replace(/[$,\s]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
}

function toText(cell: CellValue | null | undefined): string | null {
  if (cell === null || cell === undefined) return null;
  const text = String(cell).trim();
  return text === "" ? null : text;
}

/** Keywords are rewritten into text Amazon accepts; rows that stay unusable are skipped silently. */
export function toImportRows(sheet: KeywordSheet, mapping: ColumnMapping): TemplateImportRow[] {
  const keywordColumn = mapping.keyword;
  if (keywordColumn === undefined) return [];
  const cell = (row: SheetData[number], field: ImportField) => {
    const index = mapping[field];
    return index === undefined ? null : row[index];
  };
  return sheet.rows.flatMap((row) => {
    const text = toText(row[keywordColumn]);
    const keyword = text && toAmazonKeywordText(text);
    if (!keyword) return [];
    return [
      {
        keyword,
        translation: toText(cell(row, "translation")),
        relevance: toNumber(cell(row, "relevance")),
        abaMonthlyRank: toNumber(cell(row, "abaMonthlyRank")),
        monthlySearchVolume: toNumber(cell(row, "monthlySearchVolume")),
        monthlyPurchases: toNumber(cell(row, "monthlyPurchases")),
        ppcBid: toNumber(cell(row, "ppcBid")),
        category: toText(cell(row, "category")),
      },
    ];
  });
}
